import { Component, OnInit, AfterViewInit, ElementRef, ViewChild, Input, inject, signal, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VehicleTooltipComponent, VehicleTooltipData } from '@modules/shared/components/vehicle-tooltip/vehicle-tooltip';

import mapboxgl from 'mapbox-gl';

import { ConfigService } from '../../../../config';
import { MapStateService } from '../map-state.service';

import * as _ from 'lodash';
import { MapState } from '../models';
import { MapboxService } from './mapbox-map.service';
import { MapStyle } from '../../shared/models';
import { NotificationService } from '../../shared/services/notification.service';
import { TranslateService } from '../../core/services';
import { Clipboard } from '@angular/cdk/clipboard';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { HtmlPopupHelper } from './html-popup-helper';


@Component({
    selector: 'places-mapbox-map',
    templateUrl: './mapbox-map.component.html',
    styleUrls: ['./mapbox-map.component.scss'],
    imports: [TranslateModule, VehicleTooltipComponent],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class MapboxMapComponent implements OnInit, AfterViewInit {
    private readonly _configService = inject(ConfigService);
    private readonly _element = inject(ElementRef);
    private readonly _mapboxService = inject(MapboxService);
    private readonly _mapstateService = inject(MapStateService);
    private readonly _clipboard = inject(Clipboard);
    private readonly _notificationService = inject(NotificationService);
    private readonly _translate = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly _htmlPopupHelper = inject(HtmlPopupHelper);
    private readonly _destroyRef = inject(DestroyRef);

    readonly vehicleTooltipData = signal<VehicleTooltipData | null>(null);
    readonly vehicleTooltipPosition = signal<{ x: number, y: number } | null>(null);

    private _hideTooltipTimer: ReturnType<typeof setTimeout> | null = null;

    private _defaultMapStyle: MapStyle;

    public contextMenuCoords;
    public environmentName;

    @Input() public set defaultMapStyle(mapStyle: MapStyle) {
        this._defaultMapStyle = mapStyle;

        if (mapStyle?.config) {
            this._mapboxService.changeStyle(mapStyle);
        }
    }

    @Input() public set defaultCenter(center: [number, number]) {
        if (center) {
            this._mapboxService.setCenter(center);
        }
    }

    public get defaultMapStyle() {
        return this._defaultMapStyle;
    }

    @ViewChild('userLocation', { static: true }) userLocation: ElementRef;


    constructor() {}

    ngOnInit() {
        const mapState: MapState = this._mapstateService.state();
        mapboxgl.accessToken = this._getMapboxAccessToken();

        const mapboxConfig: mapboxgl.MapboxOptions = {
            container: 'map',
            style: this._getMapboxMapDefaultStyle(),
            zoom: mapState.zoom,
            boxZoom: false,
            preserveDrawingBuffer: false, // set to true if we want to get the canvas exported as png
            maxZoom: 22,
            attributionControl: false,
            logoPosition: 'bottom-left',
            fadeDuration: 0,
            pitch: 0,
            pitchWithRotate: false,
            touchPitch: false
        };

        const mapboxMap = new mapboxgl.Map(mapboxConfig);
        this._mapboxService.initMap(mapboxMap, this.defaultMapStyle);
        this._checkMapElementSize();

        // listens to user rightclick in the map to place
        this._mapstateService
            .getMapPropertyState('rightClicked')
            .subscribe((clickedCoordinate: { lng: number; lat: number; x: number; y: number }) => {
                this.contextMenuCoords = clickedCoordinate;
            });

        // listens to user leftclick in the map to hide contextMenuCoords
        this._mapstateService.getMapPropertyState('leftClicked').subscribe(() => {
            this.contextMenuCoords = null;
        });

        this.environmentName = this._configService.environmentName;
    }

    public copyCoord() {
        this._clipboard.copy(
            this._translate.instant(
                this.contextMenuCoords.lat.toFixed(5) + ', ' + this.contextMenuCoords.lng.toFixed(5)
            )
        );
        this._notificationService.openText(
            this._translate.instant(
                this.contextMenuCoords.lat.toFixed(5) +
                    ', ' +
                    this.contextMenuCoords.lng.toFixed(5) +
                    ' ' +
                    'copied to clipboard'
            )
        );
        this.contextMenuCoords = null;
    }

    public registerDamageRisk() {
        // Navigate to the register-damage-risk component
        this.router.navigate(['', { outlets: { outlet0: 'register-damage-risk', outlet1: null, outlet2: null }}]);
        this.contextMenuCoords = null;
    }

    ngAfterViewInit(): void {
        this._mapboxService.resize();
        this._setupVehicleHoverPopup();
    }

    private _getMapboxMapDefaultStyle() {
        if (this.defaultMapStyle?.config) {
            return this.defaultMapStyle.config.style;
        }
        return 'mapbox://styles/nobinadevelopment/ckmouq6kd4kno17qn7l1x55e8';
    }

    private _getMapboxAccessToken() {
        return this._configService.mapboxAccessToken;
    }

    private _setupVehicleHoverPopup() {
        this._mapboxService.vehicleHover$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(async (event) => {
            if (!event) {
                this._scheduleTooltipHide();
                return;
            }

            if (this._hideTooltipTimer !== null) {
                clearTimeout(this._hideTooltipTimer);
                this._hideTooltipTimer = null;
            }

            const { vehicle, clientX, clientY } = event;
            this.vehicleTooltipPosition.set({ x: clientX, y: clientY });

            const dep = vehicle.departureDeviation;
            this.vehicleTooltipData.set({
                inventoryNumber: vehicle.inventoryNumber,
                deviationText: dep?.deviation ? dep.text : undefined,
                deviationMinutes: dep?.deviation ? Math.round(Math.abs(dep.deviation.getTotalSeconds() / 60)) : undefined,
                deviationColor: dep?.color,
                isElectric: vehicle.isElectric,
                stateOfCharge: vehicle.stateOfCharge,
                activitiesLoading: true,
                warnings: vehicle.displayableWarnings,
            });

            const activities = await this._htmlPopupHelper.getActivitiesForVehicle(vehicle);
            if (this.vehicleTooltipData()?.inventoryNumber === vehicle.inventoryNumber) {
                const resolved = this._htmlPopupHelper.resolveActivities(vehicle, activities);
                this.vehicleTooltipData.update(d => d ? {
                    ...d,
                    currentActivity: resolved.currentActivity,
                    nextActivity: resolved.nextActivity,
                    activitiesLoading: false,
                } : d);
            }
        });
    }

    onTooltipEnter(): void {
        if (this._hideTooltipTimer !== null) {
            clearTimeout(this._hideTooltipTimer);
            this._hideTooltipTimer = null;
        }
    }

    onTooltipLeave(): void {
        this._scheduleTooltipHide();
    }

    private _scheduleTooltipHide(): void {
        if (this._hideTooltipTimer !== null) return;
        this._hideTooltipTimer = setTimeout(() => {
            this.vehicleTooltipData.set(null);
            this.vehicleTooltipPosition.set(null);
            this._hideTooltipTimer = null;
        }, 80);
    }

    // Hanterar att kartan inte fastnar i fel storlek...
    private _checkMapElementSize() {
        window.setInterval(() => {
            if (!this._element.nativeElement) return;

            const elementWidth = this._element.nativeElement.clientWidth;
            const canvasWidth = this._element.nativeElement.querySelector('.mapboxgl-canvas')?.clientWidth;

            if (elementWidth > 0 && elementWidth !== canvasWidth) {
                this._mapboxService.resize();
            }
        }, 500);
    }
}
